package gr.tech.pdfgenerator.components.generation

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.AnimationIterationCount
import com.varabyte.kobweb.compose.css.FontStyle
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.autoLength
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontStyle
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.icons.fa.FaArrowLeft
import com.varabyte.kobweb.silk.components.icons.fa.FaCheck
import com.varabyte.kobweb.silk.components.icons.fa.FaCircleNotch
import com.varabyte.kobweb.silk.components.icons.fa.FaFloppyDisk
import com.varabyte.kobweb.silk.components.icons.fa.FaXmark
import com.varabyte.kobweb.silk.components.icons.fa.IconStyle
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.pdfgenerator.infrastructure.api.savePDFWithProjectID
import gr.tech.pdfgenerator.infrastructure.data.GeneratorData
import gr.tech.pdfgenerator.infrastructure.pdf.GeneratePDFTool
import gr.tech.pdfgenerator.infrastructure.pdf.PDFGenStatus
import gr.tech.pdfgenerator.styles.PDFBackButtonStyle
import gr.tech.pdfgenerator.styles.PDFColumnOverlay
import gr.tech.pdfgenerator.styles.PDFLoadingSpinnerAnimation
import gr.tech.pdfgenerator.styles.PDFStatusTextStyle
import gr.tech.pdfgenerator.util.CONSTANTS
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.AutoComplete.Companion.on
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Text
import org.w3c.files.Blob

@Composable
fun PDFGenerationUI(
    generator: GeneratePDFTool,
    status: PDFGenStatus,
    onStatusUpdate: (PDFGenStatus) -> Unit,
    onSave: (Blob) -> Unit,
    onGoBack: () -> Unit,
) {

    // Generation UI
    Column(
        modifier = PDFColumnOverlay.toModifier(),
        horizontalAlignment = Alignment.Start
    ) {
        Button(
            modifier = PDFBackButtonStyle.toModifier()
                .margin(bottom = 16.px),
            onClick = {
                onGoBack()
            }
        ) {
            FaArrowLeft(Modifier.margin(right = 4.px))
            Text("Go Back")
        }

        HorizontalDivider(Modifier.margin(bottom = 16.px).backgroundColor(Colors.Black))

        H3(
            attrs = PDFStatusTextStyle.toModifier()
                .color(Colors.Black)
                .fontWeight(FontWeight.Bold)
                .fontSize(1.7.cssRem)
                .margin(bottom = 16.px)
                .toAttrs()
        ) {
            Text("Preview")
        }

        H3(
            attrs = PDFStatusTextStyle.toModifier()
                .toAttrs()
        ) {
            Text("Status:")
        }

        PDFStatusText(
            status = status
        )

        Button(
            modifier = PDFBackButtonStyle.toModifier()
                .margin(top = 16.px),
            onClick = {
                onStatusUpdate(PDFGenStatus.DOWNLOADING)
                generator.getBlob(
                    onBlobReady = { blob ->
                        generator.downloadPDFtoBrowser(
                            onError = {
                                console.error("PDF Download failed!")
                            }
                        )
                        onSave(blob)
                    },
                    onError = {
                        onStatusUpdate(PDFGenStatus.FAILED)
                        console.error("Blob generation failed!")
                    }
                )
            }
        ) {
            FaFloppyDisk(Modifier.margin(right = 4.px))
            Text("Download & Save")
        }

        SpanText(
            text = "(Saves to browser & database)",
            modifier = Modifier
                .fillMaxWidth()
                .margin(
                    leftRight = autoLength,
                    bottom = 16.px
                )
                .textAlign(TextAlign.Center)
                .color(Colors.Black)
                .fontSize(1.cssRem)
                .fontWeight(FontWeight.Light)
                .fontStyle(FontStyle.Italic)
        )
    }
}

@Composable
fun PDFStatusText(
    status: PDFGenStatus
) {
    H3(
        attrs = PDFStatusTextStyle.toModifier()
            .toAttrs()
    ) {
        when (status) {
            PDFGenStatus.GENERATING -> {
                FaCircleNotch(Modifier
                    .animation(
                        PDFLoadingSpinnerAnimation
                            .toAnimation(
                                duration = 1500.ms,
                                timingFunction = AnimationTimingFunction.Linear,
                                iterationCount = AnimationIterationCount.Infinite,
                            )
                    )
                    .margin(right = 6.px))
                Text("Generating")
            }
            PDFGenStatus.READY_DOWNLOAD -> {
                FaFloppyDisk(Modifier.margin(right = 4.px), style = IconStyle.FILLED)
                Text("Ready to Save")
            }
            PDFGenStatus.DOWNLOADING -> {
                FaCircleNotch(Modifier
                    .animation(
                        PDFLoadingSpinnerAnimation
                            .toAnimation(
                                duration = 1500.ms,
                                timingFunction = AnimationTimingFunction.Linear,
                                iterationCount = AnimationIterationCount.Infinite,
                            )
                    )
                    .margin(right = 6.px))
                Text("Saving")
            }
            PDFGenStatus.DOWNLOADED -> {
                FaCheck(Modifier.margin(right = 4.px))
                Text("Complete")
            }
            PDFGenStatus.FAILED -> {
                FaXmark(Modifier.margin(right = 4.px))
                Text("Failed!")
            }
            PDFGenStatus.BLOB_READY -> {
                Text("Blob Ready!")
            }
        }
    }
}